/* ============================================================================
   ATD - Video 1 — controlador de reproducción (solo video SUBIDO)
   Web component <atd-video1-player data-mode="...">.

   Modos:
   - autoplay_muted  : el <video> ya trae autoplay muted loop desde Liquid.
                       Aquí no hacemos nada (el navegador lo arranca solo).
   - autoplay_unmute : arranca muted; un botón 🔊 hace video.muted = false
                       (unmute permitido porque es un gesto de usuario).
   - click_sound     : el <video> NO trae autoplay ni muted. Muestra póster +
                       botón de play; al hacer clic llamamos video.play()
                       SINCRÓNICAMENTE dentro del handler del gesto para que
                       iOS/Safari/Chrome permitan el audio.

   Sin dependencias. Idempotente (marca cada instancia con dataset.atdReady).
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('atd-video1-player')) {
    return;
  }

  class ATDVideo1Player extends HTMLElement {
    connectedCallback() {
      if (this.dataset.atdReady === 'true') return;
      this.dataset.atdReady = 'true';

      this.video = this.querySelector('[data-atd-video1]');
      if (!this.video) return;

      this.mode = this.dataset.mode || 'autoplay_muted';
      this.playBtn = this.querySelector('[data-atd-video1-play]');
      this.soundBtn = this.querySelector('[data-atd-video1-sound]');

      this._setupViewportPreload();

      if (this.mode === 'click_sound') {
        this._setupClickSound();
      } else if (this.mode === 'autoplay_unmute') {
        this._setupAutoplayUnmute();
      }
      // autoplay_muted: nada que hacer, el markup ya trae autoplay muted loop.
    }

    /**
     * Precarga al entrar en pantalla.
     *
     * Con preload="metadata" el navegador solo trae los datos básicos: al dar
     * clic arranca la descarga en frío y el video tarda segundos en empezar
     * (reportado por la clienta de Macaria, 2026-08-14). Con preload="auto" de
     * entrada, en cambio, TODO visitante se descarga el video aunque nunca baje
     * hasta él — caro en móvil y malo para las métricas de carga.
     *
     * El punto medio: empezar a bufferear cuando la sección entra en pantalla,
     * que es cuando el visitante puede darle clic. Si nunca llega, no se
     * descarga nada.
     */
    _setupViewportPreload() {
      if (!this.video.hasAttribute('data-atd-video1-preload-viewport')) return;

      // Sin IntersectionObserver (navegador viejo) se queda como está: solo
      // metadatos. Degradar es preferible a precargar a ciegas.
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            // Si ya empezó a reproducirse, load() lo reiniciaría: no tocar.
            if (!this.video.paused || this.video.currentTime > 0) return;
            this.video.preload = 'auto';
            this.video.load();
          });
        },
        { rootMargin: '200px' }
      );

      observer.observe(this);
    }

    _setupClickSound() {
      if (!this.playBtn) return;

      // Estado inicial: video pausado, con póster y botón visibles.
      this.classList.add('atd-video1--awaiting-play');

      // Reproduce CON sonido (el gesto del usuario lo autoriza). NUNCA activamos
      // los controles nativos del <video>: así iOS/Safari no pintan su barra de
      // segundos / pantalla completa / PiP. El play/pausa lo manejamos nosotros
      // por clic (mismo patrón que el hero de e-sorella).
      const playWithSound = () => {
        const p = this.video.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => {
            // Si el navegador bloquea el play con sonido, reintenta mudo
            // para no dejar el video roto.
            this.video.muted = true;
            this.video.play().catch(function () {});
          });
        }
      };

      // Las clases siguen el estado real del video, venga de donde venga el
      // play/pause (botón, clic en el video, o fin del video).
      this.video.addEventListener('play', () => {
        this.classList.remove('atd-video1--awaiting-play');
        this.classList.add('atd-video1--playing');
      });
      this.video.addEventListener('pause', () => {
        this.classList.remove('atd-video1--playing');
        this.classList.add('atd-video1--awaiting-play');
      });
      this.video.addEventListener('ended', () => {
        this.classList.remove('atd-video1--playing');
        this.classList.add('atd-video1--awaiting-play');
      });

      // Botón de play (visible cuando está en póster/pausado) → reproduce.
      this.playBtn.addEventListener('click', playWithSound);

      // Ya reproduciendo, el botón queda en pointer-events:none, así que el clic
      // llega al <video> y lo pausamos. Al pausar reaparece el botón → reanuda.
      this.video.addEventListener('click', () => {
        if (!this.video.paused) this.video.pause();
      });
    }

    _setupAutoplayUnmute() {
      if (!this.soundBtn) return;

      this.soundBtn.addEventListener('click', () => {
        const nowOn = this.video.muted; // si estaba muted, lo activamos
        this.video.muted = !nowOn;
        // Asegura reproducción (algunos navegadores pausan al cambiar audio).
        const p = this.video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
        this.soundBtn.setAttribute('aria-pressed', String(nowOn));
        this.soundBtn.setAttribute(
          'aria-label',
          nowOn ? 'Silenciar' : 'Activar sonido'
        );
        this.classList.toggle('atd-video1--sound-on', nowOn);
      });
    }
  }

  window.customElements.define('atd-video1-player', ATDVideo1Player);
})();
